import {buildResponsive} from './gdi-responsive'
import {
    parseGDIViewTypes,
    FONT_STYLE_MAP,
    resolveFontStyleProps,
    resolveResponsiveFontStyleProps
} from './gdi-styles'

/* Re-exported here for backwards compatibility with consumers that imported
 * these from gdi-cta-style. The canonical home is `./gdi-styles`. */
export {FONT_STYLE_MAP, resolveFontStyleProps, resolveResponsiveFontStyleProps}

export const DEFAULT_CTA_STYLE = 'Button Solid'

/* Outer-anchor hover propagation. `data-gdi-tile-link` is set by
 * `gdiContentTileBlock` on the wrapping anchor when the tile is configured
 * as a link. CTAs styled as `Underlined Text`, `Underlined on Hover` or
 * `Mega Menu Link` mirror their `:hover` styling through the selector below
 * so hovering anywhere on the tile triggers the CTA's hover affordance. */
export const PARENT_TILE_LINK_HOVER_SELECTOR = '[data-gdi-tile-link]:hover &'
export const PARENT_TILE_LINK_HOVER_STYLES = new Set([
    'Underlined Text',
    'Underlined on Hover',
    'Mega Menu Link'
])

export const CTA_STYLE_MAP = {
    Color: 'color',
    BackgroundColor: 'backgroundColor',
    BorderColor: 'borderColor',
    MinWidth: 'minWidth',
    GDICtaBlockPadding: 'padding',
    Margin: 'margin',
    FontSize: 'fontSize'
}

const BP_EM = {md: '48em', lg: '62em'}

export const isDesktopHoverEntry = (entry) =>
    parseGDIViewTypes(entry?.ViewTypesToApply).includes('_hover')

const isDesktopAndHoverEntry = (entry) => {
    const viewTypes = parseGDIViewTypes(entry?.ViewTypesToApply)
    return viewTypes.includes('lg') && viewTypes.includes('_hover')
}

const stripDesktopHoverViewType = (entry) => {
    if (!isDesktopHoverEntry(entry)) return entry

    const regularViewTypes = entry?.ViewTypesToApply?.split(',')
        .map((viewType) => viewType.trim())
        .filter((viewType) => !parseGDIViewTypes(viewType).includes('_hover'))
        .join(',')

    return regularViewTypes ? {...entry, ViewTypesToApply: regularViewTypes} : null
}

const withBaseFallback = (value, fallback) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return {base: fallback, ...value}
    }

    return {base: value ?? fallback}
}

export const getCtaStylesForResolve = (stylesArr) => {
    if (!Array.isArray(stylesArr)) {
        return {desktopHoverEntry: undefined, stylesForResolve: stylesArr}
    }

    const desktopHoverEntry =
        stylesArr.find(isDesktopAndHoverEntry) || stylesArr.find(isDesktopHoverEntry)
    const stylesForResolve = desktopHoverEntry
        ? stylesArr.map(stripDesktopHoverViewType).filter(Boolean)
        : stylesArr

    return {desktopHoverEntry, stylesForResolve}
}

export const getCtaTextByBreakpoint = (stylesArr) =>
    withBaseFallback(buildResponsive(stylesArr, (entry) => entry?.CtaText, ''), '')

export const getCtaStyleNameByBreakpoint = (stylesArr) =>
    withBaseFallback(
        buildResponsive(stylesArr, (entry) => entry?.Style, DEFAULT_CTA_STYLE),
        DEFAULT_CTA_STYLE
    )

export const buildCtaStyleProps = (responsiveStyles, desktopHoverEntry, styleName) => {
    const directStyles = {}
    let rawMediaStyles = {}

    for (const [key, val] of Object.entries(responsiveStyles || {})) {
        if (!val) continue
        if (typeof val !== 'object') {
            directStyles[key] = val
            continue
        }

        if (val.base !== undefined) {
            directStyles[key] = val.base
        }

        for (const [bp, bpVal] of Object.entries(val)) {
            if (!bpVal) continue
            const em = BP_EM[bp]
            if (em) {
                const mq = `@media screen and (min-width: ${em})`
                rawMediaStyles[mq] = {...(rawMediaStyles[mq] || {}), [key]: bpVal}
            }
        }
    }

    if (desktopHoverEntry) {
        const lgMq = `@media screen and (min-width: ${BP_EM.lg})`
        const hoverAtLg = {}

        for (const [key, val] of Object.entries(responsiveStyles || {})) {
            if (!val) continue
            const lgVal = typeof val === 'object' ? (val.lg ?? val.md ?? val.base ?? null) : val
            if (lgVal) hoverAtLg[key] = lgVal
        }

        /* Style swap on hover: the Chakra `variant` is fixed for the lifetime of
         * the button, so when the CMS authors a different Style on the
         * WebDesktopHover entry we apply that variant's key visual properties as
         * inline `:hover` overrides. Authored color/bg/border below still win. */
        const hoverStyleName =
            typeof desktopHoverEntry.Style === 'string' ? desktopHoverEntry.Style.trim() : ''
        if (hoverStyleName && hoverStyleName !== styleName) {
            const styleOverrides = STYLE_HOVER_OVERRIDE_MAP[hoverStyleName]
            if (styleOverrides) {
                for (const [prop, val] of Object.entries(styleOverrides)) {
                    if (val === undefined) continue
                    hoverAtLg[prop] = val
                }
            }
        }

        for (const [cmsField, cssProp] of Object.entries(CTA_STYLE_MAP)) {
            const cmsVal =
                typeof desktopHoverEntry[cmsField] === 'string'
                    ? desktopHoverEntry[cmsField].trim()
                    : desktopHoverEntry[cmsField]
            if (cmsVal && cmsVal !== 'Default / Inherit') hoverAtLg[cssProp] = cmsVal
        }

        if (desktopHoverEntry.FontStyle) {
            Object.assign(hoverAtLg, resolveFontStyleProps(desktopHoverEntry.FontStyle))
        }

        rawMediaStyles = {
            ...rawMediaStyles,
            [lgMq]: {
                ...(rawMediaStyles[lgMq] || {}),
                '&:hover': hoverAtLg,
                /* Mirror the desktop hover onto a parent-anchor selector for
                 * text-link / Mega Menu Link styles so the WebDesktopHover
                 * authoring (e.g. Color swap) also fires when the wrapping
                 * `gdiContentTileBlock` anchor is hovered. */
                ...(PARENT_TILE_LINK_HOVER_STYLES.has(styleName)
                    ? {[PARENT_TILE_LINK_HOVER_SELECTOR]: hoverAtLg}
                    : {})
            }
        }
    }

    if (styleName === 'Mega Menu Link') {
        rawMediaStyles = {...rawMediaStyles, ...getMegaMenuLinkSx()}
    }

    /* Ensure that an authored BorderColor always produces a visible border,
     * regardless of the base variant. If borderColor is set in a given scope
     * (direct, per-breakpoint media query, or :hover) without an accompanying
     * borderWidth, default to a 1px solid border so the color actually paints. */
    ensureBorderForColor(directStyles)
    for (const scope of Object.values(rawMediaStyles)) {
        if (scope && typeof scope === 'object') {
            ensureBorderForColor(scope)
            if (scope['&:hover']) ensureBorderForColor(scope['&:hover'])
        }
    }

    return {
        directStyles,
        sxStyles: Object.keys(rawMediaStyles).length ? rawMediaStyles : undefined
    }
}

const ensureBorderForColor = (target) => {
    if (!target || typeof target !== 'object') return
    if (!target.borderColor) return
    if (!target.borderWidth) target.borderWidth = '1px'
    if (!target.borderStyle) target.borderStyle = 'solid'
}

/* Shared mapping between the CMS "Style" dropdown and the design-system button
 * variants declared in theme/components/base/button.js. Extracted from
 * gdiCtaBlock so other consumers (e.g. the footer navigation links) can render
 * CTAs with identical visual treatment without duplicating the table. */
export const STYLE_VARIANT_MAP = {
    'Button Solid': 'webPrimarySmall',
    'Button Solid Reverse': 'webPrimaryReverse',
    'Outline': 'webSecondaryOutlineTransparentSmall',
    'Button Outline Reverse': 'webSecondaryOutlineReverse',
    'Underlined Text': 'webTertiary',
    'Underlined on Hover': 'webTertiary',
    'Mega Menu Link': 'ghost',
    /* Internal-only: nav-link typography with a plain text-decoration underline
     * on hover (no animated pseudo-element). Used by the CMS-driven mega menu
     * when an author picks "Underlined on Hover" so they get the same look as
     * the rest of the navigation without opting into the animated style. */
    'Mega Menu Link Static': 'webTertiary'
}

/* The Chakra `variant` prop maps to a single class and cannot change at a CSS
 * breakpoint or pseudo-state. To support per-breakpoint Style swapping (e.g.
 * mobile uses 'Button Solid' but desktop hover swaps to 'Outline'), we capture
 * the essential visual properties of each Style here so they can be applied
 * inline as `:hover` overrides at the lg breakpoint. CMS-authored color /
 * backgroundColor / borderColor still win over these defaults. */
export const STYLE_HOVER_OVERRIDE_MAP = {
    'Button Solid': {
        backgroundColor: 'webButton.btnPrimaryDefault',
        color: 'webTypo.textPrimaryReverse',
        borderWidth: '0',
        textDecoration: 'none',
        padding: undefined
    },
    'Button Solid Reverse': {
        backgroundColor: 'webButton.btnPrimaryReverseDefault',
        color: 'webTypo.textPrimary',
        borderWidth: '0',
        textDecoration: 'none',
        padding: undefined
    },
    'Outline': {
        backgroundColor: 'transparent',
        color: 'webTypo.textPrimary',
        borderWidth: '1px',
        borderColor: 'webTypo.textPrimary',
        textDecoration: 'none',
        padding: undefined
    },
    'Button Outline Reverse': {
        backgroundColor: 'transparent',
        color: 'webTypo.textPrimaryReverse',
        borderWidth: '1px',
        borderColor: 'webTypo.textPrimaryReverse',
        textDecoration: 'none',
        padding: undefined
    },
    'Underlined Text': {
        backgroundColor: 'transparent',
        color: 'webButton.btnTertiaryTextDefault',
        borderWidth: '0',
        textDecoration: 'underline',
        padding: '0'
    },
    'Underlined on Hover': {
        backgroundColor: 'transparent',
        color: 'webButton.btnTertiaryTextDefault',
        borderWidth: '0',
        textDecoration: 'underline',
        padding: '0'
    }
}

export const getCtaVariant = (style) =>
    STYLE_VARIANT_MAP[style] || STYLE_VARIANT_MAP[DEFAULT_CTA_STYLE]

/* "Underlined on Hover" reuses the webTertiary variant but flips the underline
 * from default-on to hover-on. "Mega Menu Link" applies nav-tab typography.
 * Anything else returns no extras. */
export const extraStylePropsFor = (style) => {
    if (style === 'Underlined on Hover')
        return {textDecoration: 'none', _hover: {textDecoration: 'underline'}}
    if (style === 'Mega Menu Link')
        return {
            fontSize: '14px',
            lineHeight: '20px',
            fontWeight: 'medium',
            color: 'webTypo.textPrimary',
            letterSpacing: 0,
            whiteSpace: 'nowrap',
            position: 'relative',
            backgroundColor: 'transparent',
            height: 'auto',
            paddingBottom: '8px',
            px: 0,
            _hover: {textDecoration: 'none', backgroundColor: 'transparent'}
        }
    if (style === 'Mega Menu Link Static')
        return {
            fontSize: '14px',
            lineHeight: '20px',
            fontWeight: 'medium',
            color: 'webTypo.textPrimary',
            letterSpacing: 0,
            whiteSpace: 'nowrap',
            textTransform: 'none',
            backgroundColor: 'transparent',
            height: 'auto',
            marginBottom: '12px',
            mx: 0,
            textDecoration: 'none',
            _hover: {textDecoration: 'underline', backgroundColor: 'transparent'}
        }
    return null
}

export const getCtaInteractionProps = (extraStyleProps, hoverStyles) => {
    const variantHover = extraStyleProps?._hover
    const hasAuthoredHover = Object.keys(hoverStyles || {}).length > 0
    const hoverProp =
        hasAuthoredHover || variantHover
            ? {...(variantHover || {}), ...(hoverStyles || {})}
            : undefined
    const extraSpread = extraStyleProps
        ? Object.fromEntries(Object.entries(extraStyleProps).filter(([k]) => k !== '_hover'))
        : null

    return {hoverProp, extraSpread}
}

/* Mirror a CTA's resolved `_hover` styles onto a parent-anchor selector so
 * hovering the wrapping `gdiContentTileBlock` link triggers the same hover
 * affordance as a direct cursor-on-button hover. Only opted-in styles
 * propagate (see `PARENT_TILE_LINK_HOVER_STYLES`); for everything else the
 * helper returns `null` and the merged sx is unchanged.
 *
 * Note: the underline animation for `Mega Menu Link` is mirrored inside
 * `getMegaMenuLinkSx`, and the WebDesktopHover entry's `:hover` block is
 * mirrored inside `buildCtaStyleProps` — this helper covers the
 * style-driven `_hover` (e.g. `Underlined on Hover` → `textDecoration:
 * underline`) plus any author-supplied hoverStyles folded into hoverProp. */
export const getParentTileLinkHoverSx = (styleName, hoverProp) => {
    if (!PARENT_TILE_LINK_HOVER_STYLES.has(styleName)) return null
    if (!hoverProp || Object.keys(hoverProp).length === 0) return null
    return {[PARENT_TILE_LINK_HOVER_SELECTOR]: hoverProp}
}

/* Styles that should render as plain inline text links rather than as a Chakra
 * Button. The footer navigation uses this to opt out of button rendering for
 * tertiary/text styles so authoring stays consistent with GDICtaBlock. */
export const TEXT_LINK_STYLES = new Set(['Underlined on Hover', 'Underlined Text'])

export const isTextLinkStyle = (style) => !style || TEXT_LINK_STYLES.has(style)

/* "Mega Menu Link" pseudo-element underline animation. The line scales from
 * scaleX(0) to scaleX(1) on hover (same animation as the level-0 nav triggers
 * in theme/components/project/list-menu.js → listMenuTriggerLink). Lives here
 * so any consumer rendering a Mega Menu styled CTA gets the same effect.
 *
 * Wrapper selector: the line is attached to descendant `[data-mml-text]`
 * wrappers rather than the button itself. gdiCtaBlock injects those wrappers
 * around text runs only — inline-block badges produced by shortcode
 * `{highlight: ...}` directives sit OUTSIDE the wrapper, so the absolutely-
 * positioned line cannot physically cross them. This is what restores the
 * scale-from-left animation while keeping badges visually clean. Plain-text
 * Mega Menu Link CTAs (no shortcode) get a single wrapper around the whole
 * button label; the line spans the full text width as before.
 *
 * Color: `currentColor` keeps the line in sync with the authored text Color
 * (CMS Color field on the WebTablet/WebDesktop StylesPerViewType entry).
 * `position: relative` on the wrapper is required so the absolute `::before`
 * sizes against the wrapper's bounding box, not an ancestor. `inline-block`
 * makes that bounding box hug the text run (an inline parent would give the
 * pseudo a zero-width host in some browsers). */
export function getMegaMenuLinkSx() {
    return {
        '& [data-mml-text]': {
            position: 'relative',
            display: 'inline-block',
            '&::before': {
                content: '""',
                position: 'absolute',
                bottom: '-2px',
                left: 0,
                width: '100%',
                height: '1px',
                backgroundColor: 'currentColor',
                transform: 'scaleX(0)',
                transformOrigin: 'left center'
            }
        },
        '&:hover [data-mml-text]::before': {
            transform: 'scaleX(1)',
            transition: 'transform 0.3s ease'
        },
        /* Parent-anchor hover: when this CTA is nested inside a
         * `gdiContentTileBlock` configured as a link, hovering anywhere on
         * the outer anchor (`[data-gdi-tile-link]`) animates the underline
         * in step with the direct-hover behaviour above. */
        [`${PARENT_TILE_LINK_HOVER_SELECTOR} [data-mml-text]::before`]: {
            transform: 'scaleX(1)',
            transition: 'transform 0.3s ease'
        }
    }
}

/* Buttons default to content width. Parent layouts can force direct children to stretch;
 * these defaults keep CTA buttons content-sized unless an authored width overrides them. */
export const BUTTON_LAYOUT_DEFAULTS = {
    width: 'auto',
    alignSelf: 'flex-start'
}

/* Inset focus ring so it is not clipped by parent overflow containers. */
export const FOCUS_VISIBLE = {
    outline: '2px solid var(--chakra-colors-webTheme-focus)',
    outlineOffset: '-2px',
    boxShadow: 'none'
}
